import { Action, Reducer } from 'redux';
import { AppThunkAction } from '.';
import * as ArrayUtils from "typescript-array-utils";
import { toast } from 'react-toastify';

import CartService from '../services/CartService';
import { ShoppingCart, ShoppingCartItem } from '../models/ShoppingCart';
import * as AuthorizationStore from '../store/Authorization';

import { Toasts } from '../components/Toasts';
import { calculateCartTotalQuantity, calculateGrandTotalQuantity } from '../components/shared/Utils';
import ImpactPartnerService from '../services/ImpactPartnerService';

export interface IState {
    sendingOrderIds: string[];
    sendAllFlag: number;
    shoppingCarts: ShoppingCart[];
    totalQuantity: number,
    cartToCheckout: ShoppingCart;
    isLoadingShoppingCart: boolean;
    loadingTotalQuantity: boolean;
    itemAddedToCart: boolean;
    itemRemovedFromCart: boolean,
    partnerItemsRemovedFromCart: boolean;
    shoppingCartDetails: ShoppingCart;
    isLoadingShoppingCartDetails: boolean;
    cartNameUpdating: boolean;
    cartNameUpdated: boolean;
    movingCartItem: boolean;
    updatedCartItem: boolean;
    cartAdded: boolean;
    cartDeleted: boolean;
    appendSearch: boolean;
    totalCount: number;
    pageIndex: number;
    postBack: boolean;
    cartSending: boolean;
    cartOrderSent: boolean;
}

const unloadedState: IState = {
    totalQuantity: 0,
    sendingOrderIds: [],
    sendAllFlag: Date.now(),
    shoppingCarts: [],
    totalCount: -1,
    pageIndex: 0,
    cartToCheckout: undefined,
    isLoadingShoppingCart: false,
    isLoadingShoppingCartDetails: false,
    loadingTotalQuantity: false,
    shoppingCartDetails: undefined,
    itemAddedToCart: false,
    itemRemovedFromCart: false,
    partnerItemsRemovedFromCart: false,
    cartNameUpdating: false,
    cartNameUpdated: false,
    movingCartItem: false,
    updatedCartItem: false,
    cartAdded: false,
    cartDeleted: false,
    appendSearch: false,
    postBack: false,
    cartSending: false,
    cartOrderSent: false,
};

export enum Actions {
    Add = "Add",
    Remove = "Remove",
    RemovePartnerItems = "RemovePartnerItems",
    Clear = "Clear",
    MarkCartProcessed = "MarkCartProcessed",
    SendAllEventTrigger = "SendAllEventTrigger",
    GetUserCartsEnd = "GetUserCartsEnd",
    GetUserCartsBegin = "GetUserCartsBegin",
    GetUserCartsTotalQtyBegin = "GetUserCartsTotalQtyBegin",
    GetUserCartsTotalQtyEnd = "GetUserCartsTotalQtyEnd",
    GetCart = "GetCart",
    BeginLoadCart = "BeginLoadCart",
    GetLookupUserCarts = "GetLookupUserCarts",
    CreateCart = "CreateCart",
    UpdateCartItemBegin = "UpdateCartItemBegin",
    UpdateCartItemEnd = "UpdateCartItemEnd",
    EditCartName = "EditCartName",
    BeginEditCartName = "BeginEditCartName",
    DeleteCartBegin = "DeleteCartBegin",
    DeleteCartEnd = "DeleteCartEnd",
    CartToCheckout = "CartToCheckout",
    MoveCartItem = "MoveCartItem",
    BeginMoveCartItem = "BeginMoveCartItem",
    SendCartOrder = "SendCartOrder",
    SendCartOrderBegin = "SendCartOrderBegin"
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.

// login
interface AddCartLineAction {
    type: Actions.Add;
    userId: string;
    shoppingCart: ShoppingCart;
    itemAddedToCart: boolean;
}

interface RemoveCartLineAction {
    type: Actions.Remove;
    userId: string;
    cartId: string;
    cartItemId: string;
    itemRemovedFromCart: boolean;
}

interface RemovePartnerCartLinesAction {
    type: Actions.RemovePartnerItems;
    userId: string;
    cartId: string;
    partnerId: string;
    itemsRemovedFromCart: boolean;
}

interface UpdateCartItemBeginAction {
    type: Actions.UpdateCartItemBegin;
}

interface UpdateCartItemEndAction {
    type: Actions.UpdateCartItemEnd;
    userId: string;
    cartItem: ShoppingCartItem;
}

interface MarkCartProcessedAction {
    type: Actions.MarkCartProcessed;
}

interface ClearAction {
    type: Actions.Clear;
}

interface GetUserCartsBeginAction {
    type: Actions.GetUserCartsBegin;
    appendSearch: boolean;
}

interface GetUserCartsEndAction {
    type: Actions.GetUserCartsEnd;
    userId: string;
    shoppingCarts: ShoppingCart[];
    totalCount: number;
    pageIndex: number;
}

interface GetUserCartsTotalQtyBeginAction {
    type: Actions.GetUserCartsTotalQtyBegin;
}

interface GetUserCartsTotalQtyEndAction {
    type: Actions.GetUserCartsTotalQtyEnd;
    userId: string;
    totalQuantity: number;
}

interface GetCartAction {
    type: Actions.GetCart;
    userId: string;
    shoppingCart: ShoppingCart;
}

interface BeginLoadCartAction {
    type: Actions.BeginLoadCart;
}

interface CreateCartAction {
    type: Actions.CreateCart;
    userId: string;
    shoppingCart: ShoppingCart;
    cartAdded: boolean;
}

interface BeginEditCartNameAction {
    type: Actions.BeginEditCartName;
}
interface EditCartNameAction {
    type: Actions.EditCartName;
    userId: string;
    shoppingCart: ShoppingCart;
}

interface DeleteCartBeginAction {
    type: Actions.DeleteCartBegin;
    userId: string;
}

interface DeleteCartEndAction {
    type: Actions.DeleteCartEnd;
    userId: string;
}

interface CartToCheckoutAction {
    type: Actions.CartToCheckout;
    userId: string;
    cartToCheckout: ShoppingCart;
}

interface BeginMoveCartItemAction {
    type: Actions.BeginMoveCartItem;
}

interface MoveCartItemAction {
    type: Actions.MoveCartItem;
    sourceCartId: string;
    destinationCartId: string;
    cartItemId: string;
}

interface SendCartOrderAction {
    type: Actions.SendCartOrder;
    userId: string;
}

interface SendCartOrderBeginAction {
    type: Actions.SendCartOrderBegin;
    userId: string;
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction = AddCartLineAction | RemoveCartLineAction | CreateCartAction | BeginEditCartNameAction | EditCartNameAction | DeleteCartBeginAction | DeleteCartEndAction
    | UpdateCartItemBeginAction | UpdateCartItemEndAction
    | ClearAction | MarkCartProcessedAction | GetUserCartsBeginAction | GetUserCartsEndAction | GetCartAction | CartToCheckoutAction
    | BeginLoadCartAction | RemovePartnerCartLinesAction | BeginMoveCartItemAction | MoveCartItemAction 
    | GetUserCartsTotalQtyBeginAction | GetUserCartsTotalQtyEndAction
    | SendCartOrderAction | SendCartOrderBeginAction;

export const actionCreators = {
    add: (cartId: string, cartName: string, cartItem: ShoppingCartItem): AppThunkAction<KnownAction> => async (dispatch, getState) => {

        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {

            dispatch({
                type: Actions.Add,
                userId: state.authorization.currentUser.UserId,
                shoppingCart: undefined,
                itemAddedToCart: false
            });

            const partner = AuthorizationStore.getImpactPartnerFromLocalStorage();
            const response = await CartService.addToCart(state.authorization.currentUser.UserId, partner.Id, cartId, cartName, cartItem);

            if (!response.hasErrors && response.statusCode === 200) {
                Toasts.productAddedToCartSuccess({ cartName });

                dispatch({
                    type: Actions.Add,
                    userId: state.authorization.currentUser.UserId,
                    shoppingCart: response.value,
                    itemAddedToCart: true
                });
            }
            else {
                Toasts.productAddedToCartError();
                console.log("AddToCart", response.errorMessage);
            }
        }
    },

    remove: (cartId: string, cartItemId: string): AppThunkAction<KnownAction> => async (dispatch, getState) => {

        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {

            dispatch({
                type: Actions.Remove,
                userId: state.authorization.currentUser.UserId,
                cartId: undefined,
                cartItemId: undefined,
                itemRemovedFromCart: false
            });

            const response = await CartService.removeFromCart(cartItemId, state.authorization.currentUser.UserId);

            if (!response.hasErrors && response.statusCode === 200) {
                dispatch({
                    type: Actions.Remove,
                    userId: state.authorization.currentUser.UserId,
                    cartId: cartId,
                    cartItemId: cartItemId,
                    itemRemovedFromCart: true
                });
            }
            else {
                toast.error("Cannot remove product from Cart.");
                console.log("RemoveFromCart", response.errorMessage);
                dispatch({
                    type: Actions.Remove,
                    userId: state.authorization.currentUser.UserId,
                    cartId: undefined,
                    cartItemId: undefined,
                    itemRemovedFromCart: true
                });
            }
        }
    },
    removeCartItemsForPartner: (cartId: string, partnerId: string): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {
            dispatch({
                type: Actions.RemovePartnerItems,
                userId: state.authorization.currentUser.UserId,
                cartId: cartId,
                partnerId: partnerId,
                itemsRemovedFromCart: false
            });

            const response = await CartService.removePartnerItemsFromCart(cartId, partnerId, state.authorization.currentUser.UserId);

            if (!response.hasErrors && response.statusCode === 200) {
                dispatch({
                    type: Actions.RemovePartnerItems,
                    userId: state.authorization.currentUser.UserId,
                    cartId: cartId,
                    partnerId: partnerId,
                    itemsRemovedFromCart: true
                });
            }
            else {
                toast.error("Cannot remove partner products from Cart.");
                console.log("RemovePartnerItemsFromCart", response.errorMessage);
                dispatch({
                    type: Actions.RemovePartnerItems,
                    userId: state.authorization.currentUser.UserId,
                    cartId: cartId,
                    partnerId: partnerId,
                    itemsRemovedFromCart: false
                });
            }
        }
    },

    updateQuantity: (cartItemId: string, amount: number, approvedBy?: string): AppThunkAction<KnownAction> => async (dispatch, getState) => {

        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {

            dispatch({
                type: Actions.UpdateCartItemBegin,
            });

            const response = await CartService.updateQuantity(cartItemId, state.authorization.currentUser.UserId, amount, approvedBy);

            if (!response.hasErrors && response.statusCode === 200) {
                dispatch({
                    type: Actions.UpdateCartItemEnd,
                    userId: state.authorization.currentUser.UserId,
                    cartItem: response.value
                });
            }
            else {
                toast.error("Product quantity not updated.");
                console.log("UpdateQuantity", response.errorMessage);
            }
        }
    },

    clear: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {

        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {

            dispatch({ type: Actions.Clear })
        }
    },

    getCart: (cartId: string): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {

            dispatch({
                type: Actions.BeginLoadCart
            });

            const partner = AuthorizationStore.getImpactPartnerFromLocalStorage();
            const response = await CartService.getCart(state.authorization.currentUser.UserId, partner.Id, cartId);

            dispatch({
                type: Actions.GetCart,
                userId: state.authorization.currentUser.UserId,
                shoppingCart: !response.hasErrors ? response.value : undefined
            });
        }
    },

    getUserCarts: (pageIndex: number, pageSize: number, appendSearch: boolean): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {

            dispatch({
                type: Actions.GetUserCartsBegin,
                appendSearch,
            });

            const partner = AuthorizationStore.getImpactPartnerFromLocalStorage();
            const response = await CartService.getCarts(state.authorization.currentUser.UserId, partner.Id, pageIndex, pageSize);

            dispatch({
                type: Actions.GetUserCartsEnd,
                userId: state.authorization.currentUser.UserId,
                shoppingCarts: !response.hasErrors ? response.value.Items : [],
                totalCount: response.value.TotalItems,
                pageIndex
            });
        }
    },

    getUserCartsTotalQty: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {

            dispatch({
                type: Actions.GetUserCartsTotalQtyBegin,
            });

            const partner = AuthorizationStore.getImpactPartnerFromLocalStorage();
            const response = await CartService.getCartsTotalQty(state.authorization.currentUser.UserId, partner.Id);

            dispatch({
                type: Actions.GetUserCartsTotalQtyEnd,
                userId: state.authorization.currentUser.UserId,
                totalQuantity: response.value,
            });
        }
    },

    createNewCart: (cartName: string, callback: () => void): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {
            dispatch({
                type: Actions.CreateCart,
                cartAdded: false,
                userId: state.authorization.currentUser.UserId,
                shoppingCart: undefined
            });

            const partner = AuthorizationStore.getImpactPartnerFromLocalStorage();
            const response = await CartService.createNewCart(cartName, state.authorization.currentUser.UserId, partner.Id);

            if (!response.hasErrors && response.statusCode === 200) {
                Toasts.createCartSuccess({ cartName: cartName });
                dispatch({
                    type: Actions.CreateCart,
                    cartAdded: true,
                    userId: state.authorization.currentUser.UserId,
                    shoppingCart: response.value
                });
            }
            else {
                toast.error("Cannot create " + cartName);
                console.log("DeleteCart", response.errorMessage);
            }

            if (callback) {
                callback();
            }
        }
    },

    editCartName: (cartId: string, cartName: string): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {
            dispatch({
                type: Actions.BeginEditCartName
            });

            const partner = AuthorizationStore.getImpactPartnerFromLocalStorage();
            const response = await CartService.editCartName(cartId, cartName, state.authorization.currentUser.UserId, partner.Id);

            if (!response.hasErrors && response.statusCode === 200) {
                dispatch({
                    type: Actions.EditCartName,
                    userId: state.authorization.currentUser.UserId,
                    shoppingCart: response.value
                });
            }
            else {
                toast.error("Cannot change name of " + cartName);
                console.log("EditCartName", response.errorMessage);
            }
        }
    },

    deleteCart: (cart: ShoppingCart): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {
            dispatch({
                type: Actions.DeleteCartBegin,
                userId: state.authorization.currentUser.UserId,
            });

            const response = await CartService.deleteCart(cart.CartId, state.authorization.currentUser.UserId);

            if (!response.hasErrors && response.statusCode === 200) {
                Toasts.deleteCartSuccess({ cartName: cart.CartName });

                dispatch({
                    type: Actions.DeleteCartEnd,
                    userId: state.authorization.currentUser.UserId,
                });
            }
            else {
                toast.error("Cannot delete " + cart.CartName);
                console.log("DeleteCart", response.errorMessage);
            }
        }
    },

    setShoppinCart: (cart: ShoppingCart): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {

            dispatch({
                type: Actions.CartToCheckout,
                userId: state.authorization.currentUser.UserId,
                cartToCheckout: cart
            });
        }
    },

    sendCartOrder: (cart: ShoppingCart):  AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {

            dispatch({
                type: Actions.SendCartOrderBegin,
                userId: state.authorization.currentUser.UserId,
            });

            const partner = AuthorizationStore.getImpactPartnerFromLocalStorage();

            var partnerResponse = await ImpactPartnerService.get(partner.Id);
            var orderPartner = partnerResponse.value;
            const response = await CartService.sendOrder(cart.CartId, state.authorization.currentUser.UserId, orderPartner);

            if (!response.hasErrors && response.statusCode === 200) {
                Toasts.sendCartOrderSuccess({ cartName: cart.CartName });
                console.log("SendCartOrder", response.value);
            }
            else {
                toast.error("Cannot send cart order");
                console.log("SendCartOrder", response.errorMessage);
            }

            dispatch({
                type: Actions.SendCartOrder,
                userId: state.authorization.currentUser.UserId
            });
        }
    },

    markCartProcessed: (cartId: string): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        if (state && state.authorization && state.authorization.currentUser) {
            const response = await CartService.markCartProcessed(cartId);

            if (!response.hasErrors) {
                dispatch({
                    type: Actions.MarkCartProcessed
                });
            }
            else {
                console.log("MarkCartProcessed", response.errorMessage);
            }
        }
    },

    moveCartItem: (sourceCartId: string, destCartId: string, cartItemId: string): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {
            dispatch({
                type: Actions.BeginMoveCartItem
            });

            const response = await CartService.moveCartItem(destCartId, cartItemId);

            if (!response.hasErrors) {

                dispatch({
                    type: Actions.MoveCartItem,
                    sourceCartId: sourceCartId,
                    destinationCartId: destCartId,
                    cartItemId: cartItemId
                });
            }
            else {
                toast.error("Cannot move cart item.");
                console.log("MoveCartItem", response.errorMessage);

                dispatch({
                    type: Actions.MoveCartItem,
                    sourceCartId: sourceCartId,
                    destinationCartId: destCartId,
                    cartItemId: cartItemId
                });
            }
        }
    }
};

function calculateGrandTotal(carts: ShoppingCart[]) {
    return carts.reduce((acc, val) => acc + val.TotalQuantity, 0);
}

export const reducer: Reducer<IState> = (state: IState | undefined, incomingAction: Action): IState => {
    if (state === undefined) {
        return unloadedState;
    }

    const action = incomingAction as KnownAction;
    switch (action.type) {

        // add
        case Actions.Add: {

            return {
                ...state,
                itemAddedToCart: action.itemAddedToCart,
            };
        }

        // remove
        case Actions.Remove: {

            return {
                ...state,
                itemRemovedFromCart: action.itemRemovedFromCart
            };
        }

        // remove partner items
        case Actions.RemovePartnerItems: {

            return {
                ...state,
                //shoppingCarts: newShoppingCarts,
                //totalQuantity: calculateGrandTotalQuantity(newShoppingCarts ? newShoppingCarts : []),
                partnerItemsRemovedFromCart: action.itemsRemovedFromCart
            };
        }
        case Actions.UpdateCartItemBegin: {
            return { ...state, updatedCartItem: false };
        }
        case Actions.UpdateCartItemEnd: {

            const existingCartIndex = (state.shoppingCarts || []).findIndex(x => x.CartId === action.cartItem.CartId);

            if (existingCartIndex > -1) {

                let newShoppingCarts: ShoppingCart[];

                let newCart = state.shoppingCarts[existingCartIndex];
                const cartItemIndex = (newCart.Items || []).findIndex(x => x.CartItemId === action.cartItem.CartItemId);
                if (cartItemIndex > -1) {

                    newCart.Items = ArrayUtils.replace(newCart.Items, cartItemIndex, action.cartItem);
                    newCart.TotalQuantity = calculateCartTotalQuantity(newCart.Items);

                    newShoppingCarts = ArrayUtils.replace(state.shoppingCarts, existingCartIndex, newCart);
                }

                return {
                    ...state,
                    shoppingCarts: newShoppingCarts,
                    updatedCartItem: true
                };
            }

            if (state.shoppingCartDetails && state.shoppingCartDetails.CartId === action.cartItem.CartId) {
                let newCart = Object.assign({}, state.shoppingCartDetails);

                const cartItemIndex = (newCart.Items || []).findIndex(x => x.CartItemId === action.cartItem.CartItemId);
                if (cartItemIndex > -1) {

                    newCart.Items = ArrayUtils.replace(newCart.Items, cartItemIndex, action.cartItem);
                    newCart.TotalQuantity = calculateCartTotalQuantity(newCart.Items);

                    return {
                        ...state,
                        shoppingCartDetails: newCart,
                        updatedCartItem: true
                    };
                }
            }

            return { ...state, updatedCartItem: true };
        }

        case Actions.MarkCartProcessed: {
            return { ...state };
        }

        // clear
        case Actions.Clear: {
            return {
                ...state,
                sendingOrderIds: [],
                totalQuantity: 0
            };
        }
        
        case Actions.GetUserCartsBegin: {
            return {
                ...state,
                isLoadingShoppingCart: true,
                postBack: true,
                appendSearch: action.appendSearch,
                shoppingCarts: !action.appendSearch ? [] : state.shoppingCarts,
                totalCount: !action.appendSearch ? -1 : state.totalCount,
            };
        }

        case Actions.GetUserCartsEnd: {

            (action.shoppingCarts || []).forEach((cart) => {
                cart.TotalQuantity = calculateCartTotalQuantity(cart.Items);
            });

            const newCarts: ShoppingCart[] = state.appendSearch ? [...state.shoppingCarts, ...action.shoppingCarts] : [...action.shoppingCarts]

            return {
                ...state,
                isLoadingShoppingCart: false,
                totalCount: action.totalCount,
                shoppingCarts: newCarts,
                pageIndex: action.pageIndex
            };
        }

        //
        case Actions.GetUserCartsTotalQtyBegin: {
            return {
                ...state,
                loadingTotalQuantity: true,
                totalQuantity: 0
            };
        }

        case Actions.GetUserCartsTotalQtyEnd: {

            return {
                ...state,
                totalQuantity: action.totalQuantity,
                loadingTotalQuantity: false,
                pageIndex: 0
            };
        }

        case Actions.BeginLoadCart: {
            return { ...state, isLoadingShoppingCartDetails: true };
        }

        case Actions.GetCart: {
            let newCart = action.shoppingCart;

            if (newCart !== undefined) {
                newCart.TotalQuantity = calculateCartTotalQuantity(newCart.Items);

                return {
                    ...state,
                    shoppingCartDetails: newCart,
                    isLoadingShoppingCartDetails: false,
                };
            }

            return { ...state };
        }

        case Actions.CreateCart: {

            return {
                ...state,
                cartAdded: action.cartAdded,
            };
        }

        case Actions.BeginEditCartName:
            {
                return {
                    ...state,
                    cartNameUpdating: true,
                    cartNameUpdated: false
                }
            }

        case Actions.EditCartName: {
            let newCart: ShoppingCart = null;

            const existingCartIndex = (state.shoppingCarts || []).findIndex(x => x.CartId === action.shoppingCart.CartId);

            if (existingCartIndex > -1) {

                let newShoppingCarts: ShoppingCart[];

                newShoppingCarts = state.shoppingCarts.map(item =>
                    item.CartId === action.shoppingCart.CartId ? { ...item, CartName: action.shoppingCart.CartName } : item
                );

                return {
                    ...state,
                    shoppingCarts: newShoppingCarts,
                    cartNameUpdating: false,
                    cartNameUpdated: true
                };
            }

            if (state.shoppingCartDetails && state.shoppingCartDetails.CartId === action.shoppingCart.CartId) {
                newCart = Object.assign({}, state.shoppingCartDetails);
                newCart.CartName = action.shoppingCart.CartName;

                return {
                    ...state,
                    shoppingCartDetails: newCart,
                    cartNameUpdating: false,
                    cartNameUpdated: true
                };
            }

            return {
                ...state,
                cartNameUpdating: false,
                cartNameUpdated: true
            };
        }

        case Actions.DeleteCartBegin: {

            return {
                ...state,
                cartDeleted: false,
            };
        }

        case Actions.DeleteCartEnd: {

            return {
                ...state,
                cartDeleted: true
            };
        }

        case Actions.CartToCheckout: {

            return {
                ...state,
                cartToCheckout: action.cartToCheckout
            };
        }

        case Actions.BeginMoveCartItem: {
            return {
                ...state,
                movingCartItem: true
            }
        }

        case Actions.MoveCartItem: {

            return {
                ...state,
                movingCartItem: false
            }
        }

        case Actions.SendCartOrderBegin: {

            return {
                ...state,
                cartSending: true,
                cartOrderSent: false,
            };
        }

        case Actions.SendCartOrder: {

            return {
                ...state,
                cartSending: false,
                cartOrderSent: true,
            };
        }

        default: return state;
    }
};